import { ref, inject } from 'vue'
import { getOptimizedImage, getOptimizedBackgroundImage } from '../lib/helper.js'

export function useImages() {
  const myportal = inject('myportal')
  
  // Prüfe ob MyPortal Client verfügbar ist
  if (!myportal) {
    console.error('[useImages] MyPortal client not available - was it properly injected?');
  }
  
  // Reactive refs für Bild-URLs
  const imageCache = ref(new Map())
  
  // Optimiertes Bild laden
  const loadOptimizedImage = async (imageId, width = 800, height = 600, format = 'webp', quality = 80) => {
    if (!imageId) {
      console.warn('[useImages] No image ID provided');
      return null;
    }
    
    if (!myportal) {
      console.error('[useImages] MyPortal client not available for image loading');
      throw new Error('MyPortal client not available');
    }
    
    const cacheKey = `${imageId}_${width}_${height}_${format}_${quality}`;
    
    // Prüfe Cache
    if (imageCache.value.has(cacheKey)) {
      const cachedUrl = imageCache.value.get(cacheKey);
      if (cachedUrl && cachedUrl !== 'error') {
        return cachedUrl;
      }
    }
    
    try {
      const imageUrl = await getOptimizedImage(myportal, imageId, width, height, quality);
      
      if (imageUrl) {
        imageCache.value.set(cacheKey, imageUrl);
      }
      
      return imageUrl;
    } catch (error) {
      console.error(`[useImages] Failed to load image: ${imageId}`, error);
      
      // Cache Fehler für kurze Zeit um wiederholte Requests zu vermeiden
      imageCache.value.set(cacheKey, 'error');
      setTimeout(() => {
        if (imageCache.value.get(cacheKey) === 'error') {
          imageCache.value.delete(cacheKey);
        }
      }, 30000);
      
      throw error;
    }
  }
  
  // Optimiertes Hintergrundbild laden
  const loadOptimizedBackgroundImage = async (background_image, preset = 'background') => {
    if (!background_image || !background_image.image) {
      console.warn('[useImages] No background image data provided');
      return "";
    }
    
    if (!myportal) {
      console.error('[useImages] MyPortal client not available for background image loading');
      throw new Error('MyPortal client not available');
    }
    
    try {
      return await getOptimizedBackgroundImage(myportal, background_image, preset);
    } catch (error) {
      console.error(`[useImages] Failed to load background image: ${background_image.image}`, error);
      throw error;
    }
  }
  
  // Bild für verschiedene Bildschirmgrößen laden (responsive)
  const loadResponsiveImage = async (imageId, sizes = { mobile: [400, 300], tablet: [800, 600], desktop: [1200, 900] }) => {
    const results = {};
    
    for (const [breakpoint, [width, height]] of Object.entries(sizes)) {
      try {
        results[breakpoint] = await loadOptimizedImage(imageId, width, height);
      } catch (error) {
        console.error(`[useImages] Failed to load ${breakpoint} variant for: ${imageId}`, error);
        throw error;
      }
    }
    
    return results;
  }
  
  // Cache leeren
  const clearImageCache = () => {
    // URLs freigeben um Memory Leaks zu vermeiden
    for (const url of imageCache.value.values()) {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }
    
    imageCache.value.clear();
  }
  
  // Einzelne URL freigeben
  const revokeImageUrl = (url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
      
      // Aus Cache entfernen
      for (const [key, cachedUrl] of imageCache.value.entries()) {
        if (cachedUrl === url) {
          imageCache.value.delete(key);
          break;
        }
      }
    }
  }

  return {
    loadOptimizedImage,
    loadOptimizedBackgroundImage,
    loadResponsiveImage,
    clearImageCache,
    revokeImageUrl,
    imageCache: imageCache.value
  }
}